<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useProducts, usePublisherProductMetrics } from "@/queries/products";
import {
  useActivities,
  renderActivityTitle,
  formatActivityTimestamp,
  activityIconClass,
  activityColorClass,
} from "@/queries/activities";
import { useSessionStore } from "@/stores/session-store";
import PageHeader from "@/components/PageHeader.vue";
import CreateProductModal from "./modals/CreateProductModal.vue";

const router = useRouter();
const sessionStore = useSessionStore();

const { products } = useProducts();

// Same real-metrics source the standalone PublisherProducts list uses — subscriber count,
// workflow count and success rate are real per product; we aggregate them for the top stats
// row. monthlyRevenue (no billing model) and the trend deltas ("+1 this month", etc.) have no
// data behind them yet, so the template greys those out.
const orgId = computed(() => sessionStore.session?.currentOrgId ?? "");
const { metricsByProduct } = usePublisherProductMetrics(orgId);

// "Active" = product has a published version — matches the Published/Unpublished badge below.
const activeProductCount = computed(
  () => products.value.filter((p) => p.hasPublishedVersion).length,
);
const totalSubscribers = computed(() => {
  let sum = 0;
  for (const m of metricsByProduct.value.values()) sum += m.subscriberCount;
  return sum;
});
// Total cumulative runs across all the publisher's products. We don't currently track
// runs-per-week (the metrics endpoint returns cumulative totals from RunHistory only) so
// total runs is the closest real engagement number we can show without backend changes.
const totalRuns = computed(() => {
  let sum = 0;
  for (const m of metricsByProduct.value.values()) sum += m.totalRuns;
  return sum;
});

// Placeholder — no billing model exists. Rendered with .metric-placeholder in the template.
const placeholderMonthlyRevenue = 12400;

// Create Product modal state
const showCreateProductModal = ref(false);

// Live activity feed — Publisher dashboard shows product/subscription/workflow-run events.
const { activities: recentActivity, activitiesLoading } = useActivities({
  categories: ["Product", "Subscription", "WorkflowRun"],
  take: 5,
});

const publisherProducts = computed(() => {
  return products.value.map((product) => {
    const m = metricsByProduct.value.get(product.id);
    const totalRuns = m?.totalRuns ?? 0;
    return {
      ...product,
      subscriberCount: m?.subscriberCount ?? 0,
      workflowCount: m?.workflowCount ?? 0,
      // Empty state when there are no recorded runs — see PublisherProducts for the same pattern.
      successRate: totalRuns > 0 ? `${m!.successRate.toFixed(1)}%` : "No runs yet",
      hasRuns: totalRuns > 0,
      monthlyRevenue: m?.monthlyRevenue ?? 0,
      status: product.hasPublishedVersion ? "Published" : "Unpublished",
    };
  });
});

function statusBadgeClass(status: string): string {
  return status === "Published" ? "bg-success" : "bg-secondary";
}

function viewProductDetail(productId: string) {
  router.push(`/publisher/${productId}`);
}

function createNewProduct() {
  showCreateProductModal.value = true;
}

function onProductCreated(_data: { productId: string; projectId: string }) {
  // Cache is invalidated automatically by useCreateProduct mutation
}
</script>

<template>
  <div>
    <PageHeader
      title="Publisher Dashboard"
      :breadcrumbs="[
        { label: 'Dashboard', href: '/' },
        { label: 'Publisher' }
      ]"
    >
      <template #actions>
        <router-link
          to="/publisher/analytics"
          class="bb-btn-secondary-cta"
          data-cy="view-analytics"
        >
          <i class="bi bi-graph-up me-2"></i><span class="d-none d-md-inline">View </span>Analytics
        </router-link>
        <button
          class="bb-btn-primary-cta"
          @click="createNewProduct"
          data-cy="new-product"
        >
          <i class="bi bi-plus me-2"></i>New<span class="d-none d-md-inline"> Product</span>
        </button>
      </template>
    </PageHeader>

    <!-- Key Metrics Row -->
    <div class="row g-4 mb-4">
      <!-- Active Products: real count of products with a published version. -->
      <div class="col-6 col-xl-3 col-lg-6">
        <div class="card text-center border">
          <div class="card-body py-4">
            <div class="h1 fw-bold text-primary mb-2">
              {{ activeProductCount }}
            </div>
            <div class="text-muted fw-medium mb-1 small">
              Active Products
            </div>
            <div class="text-success metric-unavailable" title="Trend tracking coming soon">
              +1 this month
            </div>
          </div>
        </div>
      </div>

      <!-- Total Subscribers: real, summed from the publisher-products metrics endpoint. -->
      <div class="col-6 col-xl-3 col-lg-6">
        <div class="card text-center border">
          <div class="card-body py-4">
            <div class="h1 fw-bold text-success mb-2">
              {{ totalSubscribers.toLocaleString() }}
            </div>
            <div class="text-muted fw-medium mb-1 small">
              Total Subscribers
            </div>
            <div class="text-success metric-unavailable" title="Trend tracking coming soon">
              +12 this week
            </div>
          </div>
        </div>
      </div>

      <!-- Total Runs: real, summed RunHistory counts across all products. Replaces the
           previous "Avg Success Rate" because runs-per-week needs time-bucketed data the
           backend doesn't expose yet. -->
      <div class="col-6 col-xl-3 col-lg-6">
        <div class="card text-center border">
          <div class="card-body py-4">
            <div class="h1 fw-bold text-warning mb-2">
              {{ totalRuns.toLocaleString() }}
            </div>
            <div class="text-muted fw-medium mb-1 small">
              Total Runs
            </div>
            <div class="text-success metric-unavailable" title="Trend tracking coming soon">
              +0.3% vs last month
            </div>
          </div>
        </div>
      </div>

      <!-- Monthly Revenue: placeholder — no billing model yet. Whole card greyed via
           .metric-placeholder. -->
      <div
        class="col-6 col-xl-3 col-lg-6"
        title="Revenue tracking coming soon — placeholder data"
      >
        <div class="card text-center border metric-placeholder">
          <div class="card-body py-4">
            <div class="h1 fw-bold text-info mb-2">
              ${{ (placeholderMonthlyRevenue / 1000).toFixed(1) }}k
            </div>
            <div class="text-muted fw-medium mb-1 small">
              Monthly Revenue
            </div>
            <div class="text-success">
              +18% vs last month
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="row g-4">
      <!-- Products List -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="card-title mb-0">My Products</h2>
              <button class="bb-btn-secondary-cta btn-sm" @click="createNewProduct">
                <i class="bi bi-plus me-1"></i> New Product
              </button>
            </div>
            <div v-if="publisherProducts.length === 0" class="text-center py-4">
              <i class="bi bi-box-seam display-4 text-muted"></i>
              <h3 class="mt-3 text-muted">No products yet</h3>
              <p class="text-muted">Create your first product to get started</p>
              <button class="bb-btn-primary-cta" @click="createNewProduct">
                <i class="bi bi-plus"></i> Create Product
              </button>
            </div>

            <div v-else class="row g-3">
              <div
                v-for="product in publisherProducts"
                :key="product.id"
                class="col-12 col-md-6"
              >
                <div
                  class="card h-100 card-hover"
                  @click="viewProductDetail(product.id)"
                >
                  <div class="card-body d-flex flex-column">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <h3 class="card-title mb-0">{{ product.name }}</h3>
                      <span class="badge ms-2" :class="statusBadgeClass(product.status)">
                        {{ product.status }}
                      </span>
                    </div>

                    <div class="mt-auto">
                      <!-- 2×2 metric grid matching PublisherProducts. Revenue greyed out (no
                           billing model yet); other metrics are real from the publisher-products
                           endpoint. -->
                      <div class="row g-2 text-muted small">
                        <div class="col-6">
                          <i class="bi bi-diagram-3 me-1"></i>{{ product.workflowCount }} workflows
                        </div>
                        <div class="col-6">
                          <i class="bi bi-people me-1"></i>{{ product.subscriberCount }} subscribers
                        </div>
                        <div
                          class="col-6"
                          :title="product.hasRuns ? '' : 'No completed runs yet for this product'"
                        >
                          <i class="bi bi-check-circle me-1"></i>
                          <template v-if="product.hasRuns">{{ product.successRate }} success</template>
                          <template v-else>{{ product.successRate }}</template>
                        </div>
                        <div
                          class="col-6 metric-unavailable"
                          title="Revenue tracking coming soon — placeholder data"
                        >
                          <i class="bi bi-currency-dollar me-1"></i>${{ product.monthlyRevenue.toLocaleString() }}/mo
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activity — fed by /api/activities, scoped to product/subscription/workflow-run categories. -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="card-title mb-0">Recent Activity</h2>
              <small v-if="activitiesLoading" class="text-muted">Loading…</small>
            </div>
            <div v-if="recentActivity.length === 0 && !activitiesLoading" class="text-muted small">
              No activity yet.
            </div>
            <div v-else class="d-flex flex-column gap-3">
              <div
                v-for="activity in recentActivity"
                :key="activity.id"
                class="d-flex"
                style="gap: 12px"
              >
                <div
                  class="d-flex align-items-center justify-content-center rounded-circle bg-body-secondary fs-6"
                  style="width: 32px; height: 32px;"
                  :class="activityColorClass(activity.severity)"
                >
                  <i :class="activityIconClass(activity)"></i>
                </div>
                <div class="flex-grow-1">
                  <p class="mb-1" style="font-size: 14px; line-height: 1.4">
                    {{ renderActivityTitle(activity) }}
                  </p>
                  <small class="text-muted">{{ formatActivityTimestamp(activity.occurredOn) }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Product Modal -->
    <CreateProductModal
      v-model:show="showCreateProductModal"
      @created="onProductCreated"
    />
  </div>
</template>

<style scoped lang="scss">
.product-item {
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.product-item:hover {
  border-color: var(--bs-primary);
  background-color: var(--bs-gray-50);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* COMMENTED OUT: Redundant - Bootstrap already handles responsive grid behavior */
/* Force column widths to ensure proper layout - 2 per row on mobile */
/*@media (max-width: 767.98px) {*/
/*  .row .col-6 {*/
/*    flex: 0 0 50%;*/
/*    max-width: 50%;*/
/*  }*/
/*}*/

/*@media (min-width: 992px) {*/
/*  .row .col-lg-6 {*/
/*    flex: 0 0 50%;*/
/*    max-width: 50%;*/
/*  }*/
/*}*/

/*@media (min-width: 1200px) {*/
/*  .row .col-xl-3 {*/
/*    flex: 0 0 25%;*/
/*    max-width: 25%;*/
/*  }*/
/*}*/

</style>
